import {
  DEFAULT_DISPLAY_ZONE,
  businessDate,
  businessDateInZone,
  daysBetween,
  formatJalali,
  instantAt,
  instantFromISOString,
  systemClock,
  toISOString,
  zonedPartsOf,
} from '@sakhtar/calendar';
import { faDigits } from '../faDigits.js';

/**
 * «امروز ۱۲:۳۰» / «فردا ۱۰:۰۰» / «۳ روز پیش» / «۱۴۰۵/۰۶/۰۲» for an ISO
 * instant, in the display zone (Tehran) — list cells show this; the exact
 * Jalali date-time goes in the cell's title for the tooltip.
 */
export function relativeWhen(iso: string, options: { readonly withTime?: boolean } = {}): string {
  try {
    const instant = instantFromISOString(iso);
    const day = businessDateInZone(instant, DEFAULT_DISPLAY_ZONE);
    const today = businessDateInZone(systemClock.now(), DEFAULT_DISPLAY_ZONE);
    const diff = daysBetween(today, day);
    const parts = zonedPartsOf(instant, DEFAULT_DISPLAY_ZONE);
    const time = faDigits(
      `${String(parts.hour).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}`,
    );
    const withTime = options.withTime ?? true;
    const label =
      diff === 0
        ? 'امروز'
        : diff === 1
          ? 'فردا'
          : diff === -1
            ? 'دیروز'
            : diff < 0 && diff >= -30
              ? `${faDigits(-diff)} روز پیش`
              : diff > 1 && diff <= 7
                ? `${faDigits(diff)} روز دیگر`
                : formatJalali(day, { persianDigits: true });
    return withTime && Math.abs(diff) <= 1 ? `${label} ${time}` : label;
  } catch {
    return '—';
  }
}

/** Exact Jalali date + time in the display zone — for tooltips. */
export function exactWhen(iso: string): string {
  try {
    const instant = instantFromISOString(iso);
    const day = businessDateInZone(instant, DEFAULT_DISPLAY_ZONE);
    const parts = zonedPartsOf(instant, DEFAULT_DISPLAY_ZONE);
    return `${formatJalali(day, { persianDigits: true })} ${faDigits(
      `${String(parts.hour).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}`,
    )}`;
  } catch {
    return '';
  }
}

/** Past its due time (an open follow-up), today, or later. */
export function dueTone(iso: string | null): 'overdue' | 'today' | 'future' | null {
  if (!iso) return null;
  try {
    const instant = instantFromISOString(iso);
    if (toISOString(instant) < toISOString(systemClock.now())) return 'overdue';
    const day = businessDateInZone(instant, DEFAULT_DISPLAY_ZONE);
    const today = businessDateInZone(systemClock.now(), DEFAULT_DISPLAY_ZONE);
    return daysBetween(today, day) === 0 ? 'today' : 'future';
  } catch {
    return null;
  }
}

/**
 * ISO instant → `YYYY-MM-DDTHH:mm` Tehran wall clock (the value
 * `JalaliDateTimeInput` takes); the inverse of `wallClockToIso`.
 */
export function isoToWallClock(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    const instant = instantFromISOString(iso);
    const day = businessDateInZone(instant, DEFAULT_DISPLAY_ZONE);
    const parts = zonedPartsOf(instant, DEFAULT_DISPLAY_ZONE);
    return `${String(day)}T${String(parts.hour).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}`;
  } catch {
    return '';
  }
}

/**
 * `YYYY-MM-DDTHH:mm` (what `JalaliDateTimeInput` produces — a Tehran wall
 * clock) → ISO instant, via the calendar package's zone-aware `instantAt`.
 */
export function wallClockToIso(value: string): string | null {
  const [date, time] = value.split('T');
  if (!date || !time) return null;
  const [hour, minute] = time.split(':').map(Number);
  if (hour === undefined || minute === undefined || Number.isNaN(hour) || Number.isNaN(minute)) {
    return null;
  }
  try {
    return toISOString(
      instantAt(businessDate(date), { zone: DEFAULT_DISPLAY_ZONE, time: { hour, minute } }),
    );
  } catch {
    return null;
  }
}
